/**
 * YoutubeMetricsPanel — Live.4 / NMW-271 (plan §8.3 Metrics stage, §8.8).
 *
 * Additive, channel-scoped panel surfacing the YouTube ingest metrics-over-time
 * derived by `channelYoutubeMetrics.getYoutubeMetricsOverTime`
 * (uptime / flap / dwell over a trailing window).
 *
 * Honest-state contract (matches the insights bundle idiom):
 *   - query === undefined            -> skeleton
 *   - no health transitions in window -> empty state
 *   - data                            -> metrics panel
 */

import React from 'react';
import {
  Panel, PageHead, StatCard, EmptyState, SkeletonStatRow, SkeletonPanel,
} from '../../components/primitives';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';

type Metrics = {
  windowStart: number;
  windowEnd: number;
  windowMs: number;
  uptimePct: number;
  flapCount: number;
  flapRatePerHour: number;
  dwellMsByStatus: Record<string, number>;
  transitionCount: number;
};

function fmtDuration(ms: number): string {
  const h = ms / 3_600_000;
  if (h >= 1) return `${h.toFixed(1)} h`;
  const m = ms / 60_000;
  return `${m.toFixed(0)} min`;
}

export function YoutubeMetricsPanel({
  channelId,
  rangeDays = 7,
}: {
  channelId: string;
  rangeDays?: number;
}) {
  const metrics = useQuery(
    api.channelYoutubeMetrics.getYoutubeMetricsOverTime,
    { channelId: channelId as any, rangeDays },
  ) as Metrics | undefined;

  if (metrics === undefined) {
    return (
      <div>
        <PageHead title="Métricas de ingesta (YouTube)" description={`Disponibilidad, inestabilidad y permanencia en los últimos ${rangeDays} días.`} />
        <SkeletonStatRow n={3} />
        <div style={{ height: 24 }} />
        <SkeletonPanel h={180} />
      </div>
    );
  }

  const dwellEntries = Object.entries(metrics.dwellMsByStatus)
    .sort((a, b) => b[1] - a[1]);

  const uptimeTone = metrics.uptimePct >= 0.99 ? 'good'
    : metrics.uptimePct >= 0.90 ? 'warn' : 'bad';
  const flapTone = metrics.flapCount === 0 ? 'good'
    : metrics.flapRatePerHour < 1 ? 'warn' : 'bad';

  return (
    <div>
      <PageHead title="Métricas de ingesta (YouTube)" description={`Disponibilidad, inestabilidad y permanencia en los últimos ${rangeDays} días.`} />

      {metrics.transitionCount === 0 ? (
        <Panel>
          <EmptyState
            icon="activity"
            title="Sin transiciones de salud registradas"
            body="Todavía no hay eventos de salud de ingesta de YouTube para este canal en la ventana seleccionada."
          />
        </Panel>
      ) : (
        <>
          <div className="circa-grid-4" style={{ marginBottom: 24 }}>
            <StatCard
              label="Disponibilidad"
              value={`${(metrics.uptimePct * 100).toFixed(2)}%`}
              tone={uptimeTone}
              icon="gauge"
            />
            <StatCard
              label="Inestabilidad (flaps)"
              value={metrics.flapCount}
              tone={flapTone}
              icon="alertCircle"
              help="Número de caídas desde un estado saludable en la ventana."
            />
            <StatCard
              label="Flaps / hora"
              value={metrics.flapRatePerHour.toFixed(3)}
              icon="activity"
            />
            <StatCard
              label="Transiciones"
              value={metrics.transitionCount}
              icon="checkCircle"
            />
          </div>

          <Panel title="Permanencia por estado" pad={0}>
            {dwellEntries.length === 0 ? (
              <div style={{ padding: 16 }}>
                <EmptyState icon="activity" title="Sin permanencia agregada" body="No hay tiempo acumulado por estado para mostrar." />
              </div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '12px 16px 8px', borderBottom: '1px solid var(--border-cool)', fontSize: 11, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
                  <span>Estado</span><span>Tiempo</span>
                </div>
                {dwellEntries.map(([status, ms], i) => (
                  <div key={status} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '13px 16px', alignItems: 'center', borderBottom: i < dwellEntries.length - 1 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
                    <span className="t-mono">{status}</span>
                    <span className="t-mono" style={{ color: 'var(--muted-slate)' }}>{fmtDuration(ms)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
